import React, {useEffect} from 'react';
import {useRouter} from 'next/router';
import cx from 'classnames';
import _ from 'lodash';

import ResourcePaywall from './ResourcePaywall';
import SoundCloudEmbed from './SoundCloudEmbed';
import SignupWall from '../LessonPage/SignupWall';
import OutlineBadge from '../LessonPage/OutlineBadge';
import Placeholder from '../LessonPage/Placeholder';
import Markdown from '../LessonPage/LessonMarkdown';
import DocumentHeader from '../../components/DocumentHeader';
import AuthorCard from '../../components/AuthorCard';
import YouTubeVideo from '../../components/YouTubeVideo';
import {VIDEO_PREVIEW_URL} from '../../constants/content';
import {useAppContext, useAnalytics} from '../../hooks';
import {getCanonicalURL} from '../../util/seo';


const ResourcePage = ({resource, hasAccess}) => {
  const {hasSubscription, dispatch} = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();

  useEffect(() => {
    tracker.resourceView({slug: resource.slug});
    dispatch({type: 'clearReferrer', payload: null});
  }, [resource.slug]);

  const showSignupWall = resource.free && !hasAccess;
  const showPaywall = !resource.free && !hasAccess;

  return (
    <div className="w-full pt-3 pb-32 px-4 sm:px-8 relative mx-auto bg-white">
      <DocumentHeader
        title={resource.title}
        isFree={resource.free}
        imgAlt={resource.title}
        metaDescription={resource.description}
        socialDescription={resource.description}
        canonicalUrl={getCanonicalURL(router.asPath)}
      />

      {/* Resource video */}
      {resource.video && (
      <div className="rounded overflow-hidden max-w-5xl mx-auto">
        <YouTubeVideo videoID={resource.video} />
      </div>
      )}

      {/* Resource title and author */}
      <div className="w-full max-w-3xl mx-auto">
        <div className="flex justify-between items-start mt-6 mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-black self-center">
            {resource.shortTitle}
          </h1>
          {!resource.free && !hasSubscription && <OutlineBadge>Premium</OutlineBadge>}
        </div>
        {resource.author && (
        <AuthorCard author={resource.author} className="mt-4 mb-6" />
        )}
      </div>

      {resource.paywalledVideo && (
      <div className="relative rounded-lg overflow-hidden max-w-5xl mx-auto bg-gray-400" style={{height: 420}}>
        <video
          className="filter blur h-full w-full object-cover"
          style={{height: 420}}
          src={VIDEO_PREVIEW_URL}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute top-0 bottom-0 left-0 right-0 bg-black bg-opacity-75" />
      </div>
      )}

      {/* Resource soundcloud */}
      {resource.soundcloud && <SoundCloudEmbed url={resource.soundcloud} />}

      {/* Resource content */}
      <div className={cx('max-w-3xl mx-auto', {hidden: resource.paywalledVideo})}>
        <h3 className="my-4 text-lg text-gray-500 font-medium leading-tight">{resource.description}</h3>
        <div className={cx({'LessonPage--blocked': !hasAccess})}>
          <Markdown placeholder={<Placeholder />} allowRaw>
            {resource.content}
          </Markdown>
        </div>
      </div>

      {/* Signup and upgrade walls */}
      <div className={cx('relative max-w-3xl mx-auto', {'-mt-64': resource.paywalledVideo})}>
        {showSignupWall && (
        <SignupWall
          onView={() => tracker.resourceViewSignupWall({slug: resource.slug})}
          onClick={(type) => tracker.resourceClickSignupWall({type, slug: resource.slug})}
        />
        )}
        {showPaywall && <ResourcePaywall slug={resource.slug} />}
      </div>
    </div>
  );
};

export default ResourcePage;
